import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import SurveyAnalyticsDashboard from './SurveyAnalyticsDashboard';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Arca2027SurveyResponses = () => {
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterProfession, setFilterProfession] = useState('');
    const [filterOrganization, setFilterOrganization] = useState('');
    const [filterStudentProfessional, setFilterStudentProfessional] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
    const [selectedResponse, setSelectedResponse] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(false);

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchResponses();
    }, [pagination.page, search, filterCountry, filterProfession, filterOrganization, filterStudentProfessional]);

    const fetchResponses = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
            if (search) params.append('search', search);
            if (filterCountry) params.append('country', filterCountry);
            if (filterProfession) params.append('profession', filterProfession);
            if (filterOrganization) params.append('organization', filterOrganization);
            if (filterStudentProfessional) params.append('studentProfessional', filterStudentProfessional);
            const res = await axios.get(`${API_URL}/survey/responses?${params}`, { headers });
            if (res.data.success) {
                setResponses(res.data.data.responses || []);
                setPagination(prev => ({
                    ...prev,
                    total: res.data.data.total || 0,
                    totalPages: res.data.data.totalPages || 0
                }));
            }
        } catch (err) {
            toast.error('Failed to load survey responses');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this response?')) return;
        try {
            await axios.delete(`${API_URL}/survey/responses/${id}`, { headers });
            toast.success('Response deleted');
            fetchResponses();
        } catch (err) {
            toast.error('Failed to delete response');
        }
    };

    const handleExport = async () => {
        try {
            const res = await axios.get(`${API_URL}/survey/export`, { headers, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `arca2027-survey-responses-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export downloaded');
        } catch (err) {
            toast.error('Failed to export');
        }
    };

    const countries = [...new Set(responses.map(r => r.country).filter(Boolean))];
    const professions = [...new Set(responses.map(r => r.profession).filter(Boolean))];
    const organizations = [...new Set(responses.map(r => r.organization).filter(Boolean))].sort();

    const getPageNumbers = () => {
        const { page, totalPages } = pagination;
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        if (page <= 4) {
            return [1, 2, 3, 4, 5, '...', totalPages];
        }
        if (page >= totalPages - 3) {
            return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        }
        return [1, '...', page - 1, page, page + 1, '...', totalPages];
    };

    if (showAnalytics) {
        return <SurveyAnalyticsDashboard onClose={() => setShowAnalytics(false)} />;
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h4 fw-bold mb-1">
                        <i className="bi bi-clipboard-data me-2 text-success"></i>
                        ARCA 2027 Survey Responses
                    </h2>
                    <p className="text-muted mb-0">{pagination.total} total responses</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-success" onClick={() => setShowAnalytics(true)}>
                        <i className="bi bi-bar-chart-line me-2"></i>Analysis
                    </button>
                    <button className="btn btn-outline-success" onClick={handleExport}>
                        <i className="bi bi-download me-2"></i>Export CSV
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="row g-3 mb-4">
                <div className="col-lg-3 col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search (country, profession, org, email...)"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                    />
                </div>
                <div className="col-lg-2 col-md-3">
                    <select className="form-select" value={filterCountry} onChange={(e) => { setFilterCountry(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Countries</option>
                        {countries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-3">
                    <select className="form-select" value={filterProfession} onChange={(e) => { setFilterProfession(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Professions</option>
                        {professions.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-3">
                    <select className="form-select" value={filterOrganization} onChange={(e) => { setFilterOrganization(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Organizations</option>
                        {organizations.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="col-lg-2 col-md-3">
                    <select className="form-select" value={filterStudentProfessional} onChange={(e) => { setFilterStudentProfessional(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Types</option>
                        <option value="Student">Student</option>
                        <option value="Professional">Professional</option>
                    </select>
                </div>
                <div className="col-lg-1 col-md-2">
                    <button className="btn btn-outline-secondary w-100" onClick={() => { setSearch(''); setFilterCountry(''); setFilterProfession(''); setFilterOrganization(''); setFilterStudentProfessional(''); }}>
                        <i className="bi bi-x-circle me-1"></i>Clear
                    </button>
                </div>
            </div>

            {/* Responses Table */}
            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>
            ) : responses.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    <p>No responses found</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>#</th>
                                <th>Profession</th>
                                <th>Country</th>
                                <th>Organization</th>
                                <th>Type</th>
                                <th>Practice Area</th>
                                <th>Interest</th>
                                <th>Email</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {responses.map((r, idx) => (
                                <tr key={r.id}>
                                    <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                                    <td>{r.profession}</td>
                                    <td>{r.country}</td>
                                    <td>{r.organization || '-'}</td>
                                    <td>{r.student_professional || '-'}</td>
                                    <td>{r.primary_practice_area || '-'}</td>
                                    <td>
                                        <span className={`badge ${r.interest_attending === 'Definitely interested' ? 'bg-success' : r.interest_attending === 'Not interested' ? 'bg-danger' : 'bg-secondary'}`}>
                                            {r.interest_attending || '-'}
                                        </span>
                                    </td>
                                    <td>{r.email || '-'}</td>
                                    <td>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                                    <td>
                                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => setSelectedResponse(r)} title="View details">
                                            <i className="bi bi-eye"></i>
                                        </button>
                                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r.id)} title="Delete">
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <nav className="mt-3">
                    <ul className="pagination justify-content-center flex-wrap">
                        <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>Previous</button>
                        </li>
                        {getPageNumbers().map((page, idx) => (
                            <li key={`${page}-${idx}`} className={`page-item ${pagination.page === page ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                                {page === '...' ? (
                                    <span className="page-link">...</span>
                                ) : (
                                    <button className="page-link" onClick={() => setPagination(p => ({ ...p, page }))}>{page}</button>
                                )}
                            </li>
                        ))}
                        <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next</button>
                        </li>
                    </ul>
                </nav>
            )}

            {/* Detail Modal */}
            {selectedResponse && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedResponse(null)}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Survey Response Details</h5>
                                <button className="btn-close" onClick={() => setSelectedResponse(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    {[
                                        ['Profession', selectedResponse.profession],
                                        ['Country', selectedResponse.country],
                                        ['Organization', selectedResponse.organization],
                                        ['Student / Professional', selectedResponse.student_professional],
                                        ['Years of Experience', selectedResponse.years_of_experience],
                                        ['Interest in Attending', selectedResponse.interest_attending],
                                        ['Participation Mode', selectedResponse.participation_mode],
                                        ['Top Motivations', selectedResponse.top_motivations],
                                        ['Preferred Topics', selectedResponse.preferred_topics],
                                        ['Preferred Formats', selectedResponse.preferred_formats],
                                        ['Submit Research', selectedResponse.submit_research],
                                        ['Encourage to Present', selectedResponse.encourage_present],
                                        ['Preferred Networking', selectedResponse.preferred_networking],
                                        ['Preferred Exhibitors', selectedResponse.preferred_exhibitors],
                                        ['Top Attendance Factors', selectedResponse.top_attendance_factors],
                                        ['Reasonable Fee', selectedResponse.reasonable_fee],
                                        ['Biggest Challenge', selectedResponse.biggest_challenge],
                                        ['ARCA Priorities', selectedResponse.arca_priorities],
                                        ['Speaker to Invite', selectedResponse.speaker_invite],
                                        ['Unique Feature', selectedResponse.unique_feature],
                                        ['Additional Comments', selectedResponse.additional_comments],
                                        ['Practice Area', selectedResponse.primary_practice_area],
                                        ['Heard About ARCA', selectedResponse.heard_about_arca],
                                        ['Interested in Volunteering', selectedResponse.interested_volunteering],
                                        ['Receive Updates', selectedResponse.receive_updates],
                                        ['Email', selectedResponse.email],
                                    ].map(([label, value]) => (
                                        <div key={label} className="col-md-6">
                                            <small className="text-muted d-block">{label}</small>
                                            <span className="fw-medium">{value || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setSelectedResponse(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default Arca2027SurveyResponses;
